/**
 * Agent activity labels in the app language.
 *
 * Producers (backend tool and activity-log labels, the computer-use composer,
 * execution resources, the Rust direct-session progress) send the English
 * string as before plus a structured label next to it:
 *
 *   { key: "desktop.in_app", args: { action: { key: "desktop.clicking" }, app: "Mail" } }
 *
 * Keys are the exact dotted strings of `tests/fixtures/activity-labels/*.json`
 * and are used unchanged as catalogue keys (no mapping). Plain args are raw
 * values (paths, commands, app names, keys, counts) and never translated; a
 * nested label is rendered first; an array renders each item and joins them
 * with the language's list format, lower-casing each item when the template
 * writes `{name|lower}`. Render at the moment the text is shown (React render
 * or toast build): the language can change, so long-lived state keeps the
 * label object, never the rendered text.
 */
import { activeLanguage, formatList, translate } from "@/i18n";
import { agentActivityDesktopMessages } from "@/i18n/messages/agent-activity-desktop";
import { agentActivityProgressMessages } from "@/i18n/messages/agent-activity-progress";
import { agentActivityResourceMessages } from "@/i18n/messages/agent-activity-resource";
import { agentActivityToolMessages } from "@/i18n/messages/agent-activity-tool";
import { agentActivityUiMessages } from "@/i18n/messages/agent-activity-ui";
import { lowerFirst } from "@/i18n/lower-first";
import { lookupMessage } from "@/i18n/runtime";
import type {
  MessageArea,
  MessageCatalogue,
  MessageValue,
  ParamValue,
} from "@/i18n/types";

export type ActivityLabelArg =
  | string
  | number
  | ActivityLabel
  | ReadonlyArray<string | ActivityLabel>;

export interface ActivityLabel {
  readonly key: string;
  readonly args?: { readonly [name: string]: ActivityLabelArg };
}

/** Producers nest up to ~10 levels; anything deeper is malformed. */
const MAX_DEPTH = 12;

const AREAS: ReadonlyArray<MessageArea<MessageCatalogue>> = [
  agentActivityToolMessages,
  agentActivityDesktopMessages,
  agentActivityResourceMessages,
  agentActivityProgressMessages,
  agentActivityUiMessages,
];

// The catalogue is typed per area; labels arrive with runtime keys, so the
// lookup goes through the untyped signature once their params are checked.
const translateKey = translate as (
  messages: MessageArea<MessageCatalogue>,
  key: string,
  params: Record<string, ParamValue>,
) => string;

const PLACEHOLDER = /\{([^}|]+)(\|lower)?\}/g;

function texts(value: MessageValue): string[] {
  return typeof value === "string" ? [value] : Object.values(value);
}

/** Every param the English template needs (`count` for plural forms). */
function requiredParams(value: MessageValue): Set<string> {
  const names = new Set<string>(typeof value === "string" ? [] : ["count"]);
  for (const text of texts(value))
    for (const match of text.matchAll(PLACEHOLDER)) names.add(match[1]!);
  return names;
}

type KeyInfo = {
  area: MessageArea<MessageCatalogue>;
  required: ReadonlySet<string>;
};

const KEYS = new Map<string, KeyInfo>(
  AREAS.flatMap((area) =>
    Object.entries(area.en).map(
      ([key, value]) =>
        [key, { area, required: requiredParams(value) }] as const,
    ),
  ),
);

/** By language and key: the params its template writes as `{name|lower}`. */
const loweredByLanguage = new Map<string, ReadonlySet<string>>();

function loweredParams({ area }: KeyInfo, key: string): ReadonlySet<string> {
  const cacheKey = `${activeLanguage()}:${key}`;
  const cached = loweredByLanguage.get(cacheKey);
  if (cached) return cached;
  const names = new Set<string>();
  const value = lookupMessage(area.area, area.en, key);
  for (const text of value === undefined ? [] : texts(value))
    for (const match of text.matchAll(PLACEHOLDER))
      if (match[2]) names.add(match[1]!);
  loweredByLanguage.set(cacheKey, names);
  return names;
}

/** English "a, b, c" like the backend; German "a, b und c". Chinese unit
 *  lists glue items together ("ab"), so it takes the enumeration comma. */
function joinList(items: string[]): string {
  return formatList(
    items,
    activeLanguage() === "zh"
      ? { type: "conjunction", style: "narrow" }
      : { type: "unit", style: "short" },
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** One plain item: a raw string or a nested label. */
function renderItem(value: unknown, depth: number): string | null {
  return typeof value === "string" ? value : renderLabel(value, depth + 1);
}

function renderArg(
  value: unknown,
  depth: number,
  lowered: boolean,
): ParamValue | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (!Array.isArray(value)) return renderItem(value, depth);
  const items: string[] = [];
  for (const item of value) {
    const rendered = renderItem(item, depth);
    if (rendered === null) return null;
    items.push(lowered ? lowerFirst(rendered) : rendered);
  }
  return items.length > 0 ? joinList(items) : null;
}

function renderLabel(label: unknown, depth: number): string | null {
  if (depth > MAX_DEPTH || !isRecord(label) || typeof label.key !== "string")
    return null;
  const info = KEYS.get(label.key);
  if (!info) return null;
  const args = label.args ?? {};
  if (!isRecord(args)) return null;
  const lowered = loweredParams(info, label.key);
  const params: Record<string, ParamValue> = {};
  for (const name of info.required) {
    const rendered = renderArg(args[name], depth, lowered.has(name));
    if (rendered === null) return null;
    params[name] = rendered;
  }
  return translateKey(info.area, label.key, params);
}

/** `label` in the app language, or `fallback` (the producer's English) when
 *  it is absent, malformed or names a key this build does not know. */
export function renderActivityLabel(label: unknown, fallback: string): string {
  return renderLabel(label, 0) ?? fallback;
}

/** A wire value kept only when it has the shape of a label; whether it
 *  renders is decided at display time. */
export function asActivityLabel(value: unknown): ActivityLabel | undefined {
  return isRecord(value) && typeof value.key === "string"
    ? (value as unknown as ActivityLabel)
    : undefined;
}

/** Whether two labels say the same thing; a replayed frame parses a fresh
 *  object for the same label. */
export function sameActivityLabel(
  a: ActivityLabel | undefined,
  b: ActivityLabel | undefined,
): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}
